/**
 * Tooltip Component
 *
 * Accessible tooltip for additional information on hover/focus.
 * Built with Radix UI for proper positioning and keyboard support.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../../lib/utils';

export interface TooltipProps {
  /**
   * Tooltip content
   */
  content: React.ReactNode;
  /**
   * Element that triggers the tooltip
   */
  children: React.ReactNode;
  /**
   * Side to display tooltip
   * @default 'top'
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Delay before showing (ms)
   * @default 200
   */
  delayDuration?: number;
  /**
   * Skip delay when moving between tooltips
   * @default true
   */
  skipDelayDuration?: number;
}

/**
 * Tooltip Provider - Wrap your app with this
 */
const TooltipProvider = TooltipPrimitive.Provider;

/**
 * Tooltip Root
 */
const TooltipRoot = TooltipPrimitive.Root;

/**
 * Tooltip Trigger
 */
const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * Tooltip Content
 */
const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      // Base styles - WCAG AA contrast
      'z-50 overflow-hidden rounded-md bg-neutral-900 px-3 py-1.5 text-sm text-white shadow-md',

      // Animation
      'animate-in fade-in-0 zoom-in-95',
      'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
      'data-[side=bottom]:slide-in-from-top-2',
      'data-[side=left]:slide-in-from-right-2',
      'data-[side=right]:slide-in-from-left-2',
      'data-[side=top]:slide-in-from-bottom-2',

      className
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * Tooltip Component (Simplified wrapper)
 *
 * @example
 * ```tsx
 * <Tooltip content="Produkt bearbeiten">
 *   <Button variant="ghost" size="sm">
 *     <Edit className="h-4 w-4" />
 *   </Button>
 * </Tooltip>
 * ```
 */
const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      content,
      children,
      side = 'top',
      delayDuration = 200,
      skipDelayDuration = 300,
      ...props
    },
    _ref
  ) => {
    return (
      <TooltipPrimitive.Provider
        delayDuration={delayDuration}
        skipDelayDuration={skipDelayDuration}
      >
        <TooltipPrimitive.Root>
          <TooltipPrimitive.Trigger asChild>
            {children}
          </TooltipPrimitive.Trigger>
          <TooltipPrimitive.Portal>
            <TooltipContent side={side} {...props}>
              {content}
              <TooltipPrimitive.Arrow className="fill-neutral-900" />
            </TooltipContent>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    );
  }
);

Tooltip.displayName = 'Tooltip';

export {
  Tooltip,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
};
